import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Divider,
  CircularProgress,
  TextField,
  Alert,
  Snackbar,
  Stack,
  Chip,
  LinearProgress,
  Collapse,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { theme } from "../../Theme";
import { PROMO_COLORS } from "./constants/colors";
import ManufacturerSelector from "./components/ManufacturerSelector";
import BrandSelector from "./components/BrandSelector";
import ClassSelector from "./components/ClassSelector";
import SKUSelector from "./components/SKUSelector";
import WeekSelector from "./components/WeekSelector";
import DiscountSlider from "./components/DiscountSlider";
import KPICards from "./components/KPICards";
import WarningsSection from "./components/WarningsSection";
import SummaryHeader from "./components/SummaryHeader";
import { usePromoCatalog } from "./hooks/usePromoCatalog";
import {
  addScenario,
  resetSimulatorInputs,
  setSelectedBrand,
  setSelectedClass,
  setSelectedManufacturer,
  setSelectedSKUs,
  setSelectedWeekStart,
  setSelectedWeekEnd,
  setDiscountPct,
  clearBatchSimulationQueue,
  selectBatchSimulationSku,
} from "../../Redux/promoSimulatorSlice/promoSimulatorSlice";
import {
  promoSimulatorService,
  PROMO_ALL_BRANDS_ID,
  PROMO_ALL_BRANDS_OPTION,
  PROMO_ALL_CLASSES_ID,
} from "./services/promoSimulatorService";

function isScenarioBrandSelected(brand) {
  return Boolean(brand) && brand !== PROMO_ALL_BRANDS_ID;
}

function scenarioInputPendingSx() {
  return {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#F4F6F9",
      "& fieldset": {
        borderColor: "#9CA8B8",
        borderStyle: "dashed",
        borderWidth: 1,
      },
      "&:hover fieldset": {
        borderColor: "#8896AA",
      },
      "&.Mui-disabled": {
        backgroundColor: "#F0F3F7",
      },
    },
    "& .MuiInputLabel-root.Mui-disabled": {
      opacity: 0.62,
    },
  };
}

function scenarioInputReadySx() {
  return {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      "& fieldset": {
        borderColor: "#D1D9E3",
        borderStyle: "solid",
        borderWidth: 1,
      },
      "&:hover fieldset": {
        borderColor: "#B8C4D3",
      },
    },
  };
}

export default function ScenarioSimulator() {
  const dispatch = useDispatch();
  const {
    selectedBrand,
    selectedManufacturer,
    selectedClass,
    selectedSKUs,
    selectedWeekStart,
    selectedWeekEnd,
    discountPct,
    scenarios,
    batchSimulationQueue,
    batchSimulationActiveSku,
  } =
    useSelector((state) => state.promoSimulator);

  const batchActive = Array.isArray(batchSimulationQueue) && batchSimulationQueue.length > 0;
  const batchActiveIndex =
    batchActive && batchSimulationActiveSku
      ? batchSimulationQueue.findIndex((i) => i.sku === batchSimulationActiveSku)
      : -1;

  const batchSimRunGenRef = useRef(0);
  const prevBatchSkuForResetRef = useRef(null);

  const [batchSimulatedSkus, setBatchSimulatedSkus] = useState([]);
  const batchQueueIdentity = useMemo(
    () =>
      batchSimulationQueue
        .map((i) => `${i.sku}\t${String(i.brand ?? "")}`)
        .join("|"),
    [batchSimulationQueue]
  );

  useEffect(() => {
    setBatchSimulatedSkus([]);
  }, [batchQueueIdentity]);

  const {
    manufacturers,
    apiBrands,
    apiClasses,
    manufacturersLoading,
    brandsLoading,
    classesLoading,
  } = usePromoCatalog(selectedManufacturer, selectedBrand, selectedClass);

  useEffect(() => {
    if (manufacturersLoading || manufacturers.length === 0) return;
    if (!selectedManufacturer || !manufacturers.includes(selectedManufacturer)) {
      dispatch(setSelectedManufacturer(manufacturers[0]));
    }
  }, [manufacturersLoading, manufacturers, selectedManufacturer, dispatch]);

  const manufacturerOptions = useMemo(
    () => manufacturers.map((m) => ({ id: m, label: m })),
    [manufacturers]
  );

  const brandOptions = useMemo(
    () => (brandsLoading ? [] : [PROMO_ALL_BRANDS_OPTION, ...apiBrands]),
    [apiBrands, brandsLoading]
  );

  const hasSelectedBrand = isScenarioBrandSelected(selectedBrand);
  const hasBrandForCatalog = Boolean(selectedBrand);

  const classOptions = useMemo(
    () => (classesLoading ? [] : apiClasses),
    [apiClasses, classesLoading]
  );

  const [skus, setSkus] = useState([]);
  const [skusLoading, setSkusLoading] = useState(false);

  const showNoSkusMessage =
    !skusLoading && hasSelectedBrand && Boolean(selectedClass) && skus.length === 0;

  const namedBrandOptions = useMemo(
    () => brandOptions.filter((b) => b.id !== PROMO_ALL_BRANDS_ID),
    [brandOptions]
  );

  const resolveBrandId = useMemo(() => {
    return (batchBrand) => {
      if (!batchBrand || batchBrand === "—") return null;
      const norm = String(batchBrand).trim().toLowerCase();
      const hit =
        namedBrandOptions.find((b) => String(b.id) === String(batchBrand)) ||
        namedBrandOptions.find((b) => String(b.label).trim().toLowerCase() === norm);
      return hit?.id ?? null;
    };
  }, [namedBrandOptions]);

  const guessBrandFromSkuName = useMemo(() => {
    return (skuName) => {
      if (!skuName || namedBrandOptions.length === 0) return null;
      const words = String(skuName).trim().split(/\s+/);
      for (let len = Math.min(3, words.length); len >= 1; len--) {
        const prefix = words.slice(0, len).join(" ").toLowerCase();
        const hit = namedBrandOptions.find(
          (b) =>
            String(b.label ?? "").trim().toLowerCase() === prefix ||
            String(b.id ?? "").trim().toLowerCase() === prefix
        );
        if (hit) return hit.id;
      }
      return null;
    };
  }, [namedBrandOptions]);

  useEffect(() => {
    if (!batchActive || !batchSimulationActiveSku || brandsLoading) return;
    const item = batchSimulationQueue.find((i) => i.sku === batchSimulationActiveSku);
    if (!item) return;

    let bid = resolveBrandId(item.brand);
    if (!bid) bid = guessBrandFromSkuName(item.sku);

    if (bid && bid !== selectedBrand) {
      dispatch(setSelectedBrand(bid));
      dispatch(setSelectedSKUs([]));
    }
    if (item.class && item.class !== selectedClass) {
      dispatch(setSelectedClass(item.class));
      dispatch(setSelectedSKUs([]));
    }
  }, [
    batchActive,
    batchSimulationActiveSku,
    batchSimulationQueue,
    brandsLoading,
    resolveBrandId,
    guessBrandFromSkuName,
    selectedBrand,
    selectedClass,
    dispatch,
  ]);

  useEffect(() => {
    if (!batchActive || !batchSimulationActiveSku) return;
    if (!hasSelectedBrand) return;
    if (skusLoading) return;
    const match = skus.find(
      (s) =>
        s.label === batchSimulationActiveSku ||
        s.id === batchSimulationActiveSku ||
        String(s.label).trim() === String(batchSimulationActiveSku).trim()
    );
    if (!match) return;
    if (selectedSKUs[0] !== match.id) {
      dispatch(setSelectedSKUs([match.id]));
    }
  }, [
    batchActive,
    batchSimulationActiveSku,
    hasSelectedBrand,
    selectedBrand,
    skus,
    skusLoading,
    selectedSKUs,
    dispatch,
  ]);
  const [weeks, setWeeks] = useState([]);
  const [weeksLoading, setWeeksLoading] = useState(false);
  const [weeksLoadError, setWeeksLoadError] = useState(null);
  const [simulationResult, setSimulationResult] = useState(null);
  const [scenarioName, setScenarioName] = useState("");
  const [running, setRunning] = useState(false);
  const [lastSavedNameTrim, setLastSavedNameTrim] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  useEffect(() => {
    if (!batchActive) {
      prevBatchSkuForResetRef.current = null;
      return;
    }
    const cur = batchSimulationActiveSku;
    const prev = prevBatchSkuForResetRef.current;
    if (prev != null && cur != null && prev !== cur) {
      batchSimRunGenRef.current += 1;
      setSimulationResult(null);
      setScenarioName("");
      setLastSavedNameTrim(null);
      setToast((t) => ({ ...t, open: false }));
      setRunning(false);
      dispatch(setSelectedWeekStart(null));
      dispatch(setSelectedWeekEnd(null));
      dispatch(setDiscountPct(0));
    }
    prevBatchSkuForResetRef.current = cur ?? null;
  }, [batchActive, batchSimulationActiveSku, dispatch]);

  const scenarioNameTrim = scenarioName.trim();
  const scenarioNameTaken = scenarios.some(
    (s) => s.name.trim().toLowerCase() === scenarioNameTrim.toLowerCase()
  );
  const isSaveLocked =
    lastSavedNameTrim !== null &&
    scenarioNameTrim === lastSavedNameTrim;
  const canSaveNew =
    Boolean(scenarioNameTrim) &&
    !isSaveLocked &&
    !scenarioNameTaken;

  useEffect(() => {
    if (!selectedBrand && selectedClass != null && selectedClass !== PROMO_ALL_CLASSES_ID) {
      dispatch(setSelectedClass(PROMO_ALL_CLASSES_ID));
    }
  }, [selectedBrand, selectedClass, dispatch]);

  const prevClassRef = useRef(selectedClass);
  useEffect(() => {
    const prev = prevClassRef.current;
    if (prev === selectedClass) return;
    prevClassRef.current = selectedClass;
    dispatch(setSelectedSKUs([]));
    dispatch(setSelectedWeekStart(null));
    dispatch(setSelectedWeekEnd(null));
  }, [selectedClass, dispatch]);

  useEffect(() => {
    if (!hasSelectedBrand || !selectedClass) {
      setSkus([]);
      setSkusLoading(false);
      return;
    }
    let ignore = false;
    setSkusLoading(true);
    promoSimulatorService
      .getSKUsByBrand(selectedBrand, selectedManufacturer, selectedClass)
      .then((rows) => {
        if (!ignore) setSkus(Array.isArray(rows) ? rows : []);
      })
      .finally(() => {
        if (!ignore) setSkusLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [selectedBrand, selectedManufacturer, selectedClass]);

  useEffect(() => {
    const primarySkuId = selectedSKUs[0];
    if (!hasSelectedBrand || !selectedClass || !primarySkuId) {
      setWeeks([]);
      setWeeksLoading(false);
      setWeeksLoadError(null);
      dispatch(setSelectedWeekStart(null));
      dispatch(setSelectedWeekEnd(null));
      return;
    }
    const skuRow = skus.find((s) => s.id === primarySkuId);
    if (!skuRow) {
      setWeeks([]);
      setWeeksLoading(false);
      setWeeksLoadError(null);
      dispatch(setSelectedWeekStart(null));
      dispatch(setSelectedWeekEnd(null));
      return;
    }
    const skuParam = skuRow.label || skuRow.id;
    let ignore = false;
    dispatch(setSelectedWeekStart(null));
    dispatch(setSelectedWeekEnd(null));
    setWeeksLoadError(null);
    setWeeksLoading(true);
    promoSimulatorService
      .getPromoWeeks(selectedBrand, skuParam, selectedManufacturer, selectedClass)
      .then(({ weeks: nums, error }) => {
        if (ignore) return;
        setWeeksLoadError(error);
        setWeeks(
          Array.isArray(nums)
            ? nums.map((n) => ({ id: n, label: `Week ${n}` }))
            : []
        );
      })
      .finally(() => {
        if (!ignore) setWeeksLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [selectedBrand, selectedClass, selectedSKUs, selectedManufacturer, skus, dispatch]);

  const brandInputSx = brandsLoading
    ? scenarioInputPendingSx()
    : scenarioInputReadySx();
  const classInputSx =
    !hasBrandForCatalog || classesLoading
      ? scenarioInputPendingSx()
      : scenarioInputReadySx();
  const skuInputSx =
    !hasSelectedBrand || !selectedClass || skusLoading
      ? scenarioInputPendingSx()
      : scenarioInputReadySx();
  const weekInputSx =
    selectedSKUs.length === 0 || weeksLoading
      ? scenarioInputPendingSx()
      : scenarioInputReadySx();

  const canRun =
    hasSelectedBrand &&
    Boolean(selectedClass) &&
    selectedSKUs.length > 0 &&
    selectedWeekStart != null &&
    selectedWeekEnd != null &&
    Number(selectedWeekEnd) >= Number(selectedWeekStart);

  const handleRun = async () => {
    if (!canRun) return;
    const sku_names = selectedSKUs
      .map((id) => skus.find((s) => s.id === id))
      .filter(Boolean)
      .map((row) => row.label || row.id);
    if (!sku_names.length) return;

    const snapshotBatchSkuForMark =
      batchActive && batchSimulationActiveSku ? batchSimulationActiveSku : null;
    const genAtStart = batchSimRunGenRef.current;

    setRunning(true);
    setSimulationResult(null);
    setLastSavedNameTrim(null);
    try {
      const result = await promoSimulatorService.runSimulation({
        manufacturer: selectedManufacturer,
        brand: selectedBrand,
        sku_names,
        start_week: selectedWeekStart,
        end_week: selectedWeekEnd,
        discountPct,
      });
      if (batchActive && genAtStart !== batchSimRunGenRef.current) {
        return;
      }
      setSimulationResult(result);
      if (snapshotBatchSkuForMark && result && !result.error) {
        setBatchSimulatedSkus((prev) =>
          prev.includes(snapshotBatchSkuForMark)
            ? prev
            : [...prev, snapshotBatchSkuForMark]
        );
      }
    } catch (e) {
      if (batchActive && genAtStart !== batchSimRunGenRef.current) {
        return;
      }
      setSimulationResult({
        error: e?.message || "Simulation failed",
      });
    } finally {
      if (!batchActive || genAtStart === batchSimRunGenRef.current) {
        setRunning(false);
      }
    }
  };

  const handleSave = () => {
    if (!simulationResult || simulationResult.error || !scenarioNameTrim) {
      return;
    }
    if (isSaveLocked) {
      return;
    }
    if (scenarioNameTaken) {
      setToast({
        open: true,
        severity: "warning",
        message:
          "A scenario with this name already exists. Choose a different name.",
      });
      return;
    }

    const brandLabel =
      brandOptions.find((b) => b.id === selectedBrand)?.label || selectedBrand;
    const skuLabels = selectedSKUs.map(
      (id) => skus.find((s) => s.id === id)?.label || id
    );
    const name = scenarioNameTrim;
    const warnings = simulationResult.warnings || [];
    dispatch(
      addScenario({
        name,
        manufacturer: selectedManufacturer,
        brand: brandLabel,
        class: selectedClass,
        skus: skuLabels,
        weeks: { startWeek: selectedWeekStart, endWeek: selectedWeekEnd },
        discountPct,
        kpis: simulationResult.kpis,
        warnings,
        status: warnings.length > 0 ? "warning" : "active",
      })
    );
    setLastSavedNameTrim(name);
    setToast({
      open: true,
      severity: "success",
      message:
        "Scenario saved — you can open it from the Dashboard or Compare tab.",
    });
  };

  return (
    <Box>
      <Snackbar
        open={toast.open}
        autoHideDuration={toast.severity === "success" ? 4500 : 6000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          icon={
            toast.severity === "success" ? (
              <CheckCircleIcon fontSize="inherit" />
            ) : undefined
          }
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{
            alignItems: "center",
            boxShadow: 3,
            "& .MuiAlert-message": { fontWeight: 600 },
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* ── Batch Simulation Banner ── */}
      <Collapse in={batchActive} timeout={300}>
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            borderRadius: 2,
            border: `1px solid ${theme.palette.primary.main}40`,
            bgcolor: `${theme.palette.primary.main}07`,
            overflow: "hidden",
          }}
        >
          <LinearProgress
            variant="determinate"
            value={
              batchSimulationQueue.length > 0 && batchActiveIndex >= 0
                ? ((batchActiveIndex + 1) / batchSimulationQueue.length) * 100
                : 0
            }
            sx={{
              height: 3,
              bgcolor: `${theme.palette.primary.main}18`,
              "& .MuiLinearProgress-bar": { bgcolor: theme.palette.primary.main },
            }}
          />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={1.5}
            sx={{ px: 2.25, py: 1.5 }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: `${theme.palette.primary.main}18`,
                  color: theme.palette.primary.main,
                  flexShrink: 0,
                }}
              >
                <PlayArrowOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" fontWeight={700} color={theme.palette.primary.main}>
                    Simulation Skus
                  </Typography>
                  <Chip
                    label={`${batchSimulationQueue.length} SKU${batchSimulationQueue.length !== 1 ? "s" : ""}`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: `${theme.palette.primary.main}18`,
                      color: theme.palette.primary.main,
                    }}
                  />
                  <Tooltip
                    arrow
                    title="A green check means you already ran a simulation for it — you can select it again anytime."
                  >
                    <IconButton
                      size="small"
                      aria-label="About simulated SKU chips"
                      sx={{
                        color: theme.palette.primary.main,
                        p: 0.35,
                        "&:hover": { bgcolor: `${theme.palette.primary.main}14` },
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                  Click a SKU chip to load it into Scenario Inputs.
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                size="small"
                onClick={() => dispatch(clearBatchSimulationQueue())}
                sx={{ color: "text.secondary", "&:hover": { color: "text.primary" } }}
                title="End batch simulation"
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          </Stack>

          <Box
            sx={{
              px: 2.25,
              pb: 1.75,
              pt: 1,
              borderTop: `1px solid ${theme.palette.primary.main}22`,
            }}
          >
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ gap: 1 }}>
              {batchSimulationQueue.map((item, idx) => {
                const isActive = item.sku === batchSimulationActiveSku;
                const wasSimulated = batchSimulatedSkus.includes(item.sku);
                const tooltipLines = [
                  item.sku,
                  item.brand && item.brand !== "—" ? `Brand: ${item.brand}` : null,
                  wasSimulated ? "Simulated — click to open again" : "Click to load into Scenario Inputs",
                ].filter(Boolean);
                const tooltipTitle = tooltipLines.join(" · ");

                let chipColor = "default";
                let chipVariant = "outlined";
                if (isActive) {
                  chipColor = "primary";
                  chipVariant = "filled";
                } else if (wasSimulated) {
                  chipColor = "success";
                  chipVariant = "outlined";
                }

                return (
                  <Tooltip key={`${item.sku}-${idx}`} title={tooltipTitle} arrow placement="top">
                    <Chip
                      icon={
                        wasSimulated ? (
                          <CheckCircleIcon
                            sx={{
                              fontSize: "18px !important",
                              color: isActive
                                ? `${theme.palette.primary.contrastText} !important`
                                : `${theme.palette.success.main} !important`,
                            }}
                          />
                        ) : undefined
                      }
                      label={item.sku}
                      onClick={() => dispatch(selectBatchSimulationSku(item.sku))}
                      color={chipColor}
                      variant={chipVariant}
                      sx={{
                        maxWidth: { xs: "100%", sm: 300 },
                        height: "auto",
                        py: 0.5,
                        cursor: "pointer",
                        fontWeight: isActive ? 800 : 600,
                        borderWidth: wasSimulated && !isActive ? 2 : 1,
                        "& .MuiChip-label": {
                          whiteSpace: "normal",
                          textAlign: "left",
                          lineHeight: 1.35,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        },
                        "&:hover": {
                          opacity: 0.95,
                          boxShadow: 1,
                        },
                        ...(isActive &&
                          wasSimulated && {
                            boxShadow: `0 0 0 2px ${theme.palette.success.main}`,
                          }),
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Stack>
          </Box>
        </Paper>
      </Collapse>

      <SummaryHeader
        brands={brandOptions}
        classes={classOptions}
        skus={skus}
        brandsLoading={brandsLoading}
      />

      <Grid container spacing={3}>
        {/* ── Input Panel ── */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: "1px solid #F2F4F7",
              height: "100%",
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color={theme.palette.primary.main}
              mb={2}
            >
              Scenario Inputs
            </Typography>

            <Box display="flex" flexDirection="column" gap={2.5}>
              <ManufacturerSelector
                manufacturers={manufacturerOptions}
                loading={manufacturersLoading}
                formControlSx={scenarioInputReadySx()}
              />
              <BrandSelector
                brands={brandOptions}
                formControlSx={brandInputSx}
                loading={brandsLoading}
              />
              <ClassSelector
                classes={classOptions}
                formControlSx={classInputSx}
                loading={!hasBrandForCatalog || classesLoading || manufacturersLoading}
              />
              <SKUSelector skus={skus} formControlSx={skuInputSx} />
              {showNoSkusMessage && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  No SKUs found for the selected brand, class, and manufacturer.
                </Alert>
              )}
              {weeksLoadError && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  {weeksLoadError} Check your API server logs for the 500
                  traceback.
                </Alert>
              )}
              {!weeksLoadError && !weeksLoading && selectedSKUs.length > 0 && weeks.length === 0 && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  No available promotion weeks found for the selected SKU.
                </Alert>
              )}
              <WeekSelector
                weeks={weeks}
                disabled={selectedSKUs.length === 0}
                formControlSx={weekInputSx}
              />
              <DiscountSlider />
              <Divider />
              <TextField
                label="Scenario Name"
                size="small"
                value={scenarioName}
                onChange={(e) => setScenarioName(e.target.value)}
                placeholder="e.g. Q1 Summer Promo"
                fullWidth
              />
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!canRun || running}
                  startIcon={
                    running ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <PlayArrowOutlinedIcon />
                    )
                  }
                  onClick={handleRun}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: 2,
                    textTransform: "none",
                  }}
                >
                  {running ? "Running..." : "Run Simulation"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    dispatch(resetSimulatorInputs());
                    setSimulationResult(null);
                    setScenarioName("");
                    setLastSavedNameTrim(null);
                  }}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    minWidth: 0,
                    px: 1.5,
                  }}
                  title="Reset inputs"
                >
                  <RestartAltIcon />
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* ── Results Panel ── */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: "1px solid #F2F4F7",
              height: "100%",
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color={theme.palette.primary.main}
              mb={2}
            >
              Simulation Results
            </Typography>

            {!simulationResult && !running && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={8}
                color="text.secondary"
              >
                <PlayArrowOutlinedIcon
                  sx={{ fontSize: 52, mb: 1.5, opacity: 0.25 }}
                />
                <Typography>
                  Configure inputs and click Run Simulation to see results
                </Typography>
              </Box>
            )}

            {running && (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
              </Box>
            )}

            {simulationResult?.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {simulationResult.error}
              </Alert>
            )}

            {simulationResult && !simulationResult.error && (
              <Box>
                <KPICards kpis={simulationResult.kpis || {}} />

                <Box mt={2}>
                  <WarningsSection warnings={simulationResult.warnings || []} />
                </Box>
                <Divider sx={{ my: 2.5 }} />
                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                  {!scenarioNameTrim && (
                    <Typography variant="caption" color="text.secondary">
                      Enter a name above to save
                    </Typography>
                  )}
                  {scenarioNameTrim && isSaveLocked && (
                    <Typography variant="caption" color="text.secondary">
                      Saved. Change the name to save again.
                    </Typography>
                  )}
                  {scenarioNameTrim &&
                    !isSaveLocked &&
                    scenarioNameTaken && (
                      <Typography variant="caption" color="warning.main">
                        This name is already used — pick another.
                      </Typography>
                    )}
                  <Button
                    variant="contained"
                    startIcon={
                      isSaveLocked ? (
                        <CheckCircleIcon />
                      ) : (
                        <SaveOutlinedIcon />
                      )
                    }
                    disabled={!canSaveNew}
                    onClick={handleSave}
                    sx={{
                      backgroundColor: isSaveLocked
                        ? theme.palette.success.main
                        : PROMO_COLORS.safe,
                      borderRadius: 2,
                      textTransform: "none",
                      ...(isSaveLocked && {
                        "&.Mui-disabled": {
                          backgroundColor: theme.palette.success.main,
                          color: theme.palette.success.contrastText,
                          opacity: 0.92,
                        },
                      }),
                    }}
                  >
                    {isSaveLocked ? "Saved" : "Save Scenario"}
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
